import type { Extension } from '@codemirror/state';
import { StateField, RangeSetBuilder } from '@codemirror/state';
import { Decoration, DecorationSet, EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Returns a CM6 extension that highlights all case-insensitive occurrences of `query` in the document.
export function searchHighlight(query: string): Extension {
  const q = (query || '').trim();
  if (!q) {
    // Return empty array which is a valid no-op extension
    return [];
  }

  // Add error handling for regex creation
  let re: RegExp;
  try {
    re = new RegExp(escapeRegExp(q), 'gi');
  } catch (error) {
    console.error('Invalid search pattern:', error);
    return [];
  }

  const highlightMark = Decoration.mark({ class: 'cm-lexquery-search-hit' });

  // Build decorations directly using the document state without creating temporary views
  const buildDecorations = (state: EditorView['state']) => {
    const builder = new RangeSetBuilder<Decoration>();
    for (let i = 0; i < state.doc.lines; i++) {
      const line = state.doc.line(i + 1);
      const text = line.text;
      // Reset regex for each line
      re.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(text)) !== null) {
        const from = line.from + m.index;
        const to = from + m[0].length;
        builder.add(from, to, highlightMark);
      }
    }
    return builder.finish();
  };

  const field = StateField.define<DecorationSet>({
    create(state) {
      // Build initial decorations directly from state
      return buildDecorations(state);
    },
    update(decos, tr) {
      if (tr.docChanged) {
        // Recompute on any doc change, using the state directly
        return buildDecorations(tr.state);
      }
      return decos.map(tr.changes);
    },
    provide: (f) => EditorView.decorations.from(f),
  });

  // ViewPlugin to handle view-specific behavior if needed
  const plugin = ViewPlugin.fromClass(
    class {
      constructor(private view: EditorView) {}
      update(_update: ViewUpdate) {
        // Could add additional handling here if needed
      }
    }
  );

  return [field, plugin];
}
